import { LeaseColumns } from '@miq/catalog';
import moment from 'moment';

export interface TenancyDateMappingConfig {
  value: string;
  options: string[];
}

// Possible value of a date according to today's date
enum dateKey {
  Empty = 'null',
  Past = 'past',
  Future = 'future',
}

export enum TenancyColumnFields {
  OccupancyDate = 'occupancy_date',
  StartDate = 'arrangement_start_date',
  EndDate = 'arrangement_end_date',
}

export enum FloorSuiteTenancyColumnFields {
  OccupancyDate = 'occupancy_date',
  StartDate = 'tenancy_start_date',
  EndDate = 'tenancy_end_date',
}

// Possible LOV values for the Tenancy Status field
export const DefaultLOVKeys = Object.freeze({
  Unknown: 'Unknown',
  Expired: 'Expired',
  Occupying: 'Occupying',
  Future: 'Future',
  Vacated: 'Vacated',
  InMarket: 'In the Market',
  Concluded: 'Concluded',
  Leaving: 'Leaving',
});

// Variables used when all the options should be disabled
// for example for the invalid combination of dates
export const ALL_DISABLED = '__ALL_DISABLED';
const DISABLE_ALL = Object.freeze({
  value: ALL_DISABLED,
  options: [ALL_DISABLED],
});

/**
 * Cases for the possible valued according to the Dates:
 * OccupyingDate-StartDate-EndDate
 */
const DATES_MAPPING_CASES = Object.freeze({
  [`${dateKey.Empty}_${dateKey.Empty}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Unknown,
    options: [
      DefaultLOVKeys.Unknown,
      DefaultLOVKeys.Occupying,
      DefaultLOVKeys.InMarket,
      DefaultLOVKeys.Leaving,
      DefaultLOVKeys.Vacated,
    ],
  },
  [`${dateKey.Empty}_${dateKey.Empty}_${dateKey.Past}`]: {
    value: DefaultLOVKeys.Concluded,
    options: [DefaultLOVKeys.Concluded, DefaultLOVKeys.Expired, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Empty}_${dateKey.Empty}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving],
  },
  [`${dateKey.Empty}_${dateKey.Past}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Empty}_${dateKey.Past}_${dateKey.Past}`]: {
    value: DefaultLOVKeys.Concluded,
    options: [DefaultLOVKeys.Concluded, DefaultLOVKeys.Expired, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Empty}_${dateKey.Past}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving],
  },
  [`${dateKey.Empty}_${dateKey.Future}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
  // null - future - past - Invalid combination should not be possible
  [`${dateKey.Empty}_${dateKey.Future}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
  // Empty row
  [`${dateKey.Past}_${dateKey.Empty}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Past}_${dateKey.Empty}_${dateKey.Past}`]: {
    value: DefaultLOVKeys.Concluded,
    options: [DefaultLOVKeys.Concluded, DefaultLOVKeys.Expired, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Past}_${dateKey.Empty}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving],
  },
  [`${dateKey.Past}_${dateKey.Past}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Past}_${dateKey.Past}_${dateKey.Past}`]: {
    value: DefaultLOVKeys.Concluded,
    options: [DefaultLOVKeys.Concluded, DefaultLOVKeys.Expired, DefaultLOVKeys.Vacated],
  },
  [`${dateKey.Past}_${dateKey.Past}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving],
  },
  [`${dateKey.Past}_${dateKey.Future}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving, DefaultLOVKeys.Vacated],
  },
  // past future past - Invalid combination should not be possible
  [`${dateKey.Past}_${dateKey.Future}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Occupying,
    options: [DefaultLOVKeys.Occupying, DefaultLOVKeys.InMarket, DefaultLOVKeys.Leaving],
  },
  // Empty row
  [`${dateKey.Future}_${dateKey.Empty}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
  // future null past - Invalid combination should not be possible
  [`${dateKey.Future}_${dateKey.Empty}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },

  [`${dateKey.Future}_${dateKey.Past}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
  // future - past - past  - Invalid combination should not be possible
  [`${dateKey.Future}_${dateKey.Past}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },

  [`${dateKey.Future}_${dateKey.Future}_${dateKey.Empty}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
  // future - future - past - Invalid combination should not be possible
  [`${dateKey.Future}_${dateKey.Future}_${dateKey.Future}`]: {
    value: DefaultLOVKeys.Future,
    options: [DefaultLOVKeys.Future],
  },
});

const getDateKeyAccordingToDate = (date: string) => {
  const d = moment(date).endOf('day');
  if (!d.isValid()) {
    return dateKey.Empty;
  }
  const today = moment().startOf('day');
  return d <= today ? dateKey.Past : dateKey.Future;
};

/**
 *  Return the configurations according to the values for the Start Date, End Date and Occupancy date
 * @param occupancyDate string | data
 * @param startDate string | date
 * @param endDate  string | date
 * @returns { value: string, options: {string}[]} || null
 */
export const getTenancyStatusConfigurations = (
  occupancyDate: string | null,
  startDate: string | null,
  endDate: string | null
): TenancyDateMappingConfig => {
  const oKey = occupancyDate == null ? dateKey.Empty : getDateKeyAccordingToDate(occupancyDate);
  const startKey = startDate == null ? dateKey.Empty : getDateKeyAccordingToDate(startDate);
  const endKey = endDate == null ? dateKey.Empty : getDateKeyAccordingToDate(endDate);
  return DATES_MAPPING_CASES[`${oKey}_${startKey}_${endKey}`] ?? DISABLE_ALL;
};

/**
 * According the selected dates validate if the selected key is valid or not
 * @param lovKey string
 * @param occupancyDate string | data
 * @param startDate string | date
 * @param endDate  string | date
 * @returns boolean if the key is valid or not
 */
export const isSelectionValid = (
  lovKey: string | null,
  occupancyDate: string | null,
  startDate: string | null,
  endDate: string | null
) => {
  if (lovKey == '' || lovKey == null) {
    return true;
  }
  const configs = getTenancyStatusConfigurations(occupancyDate, startDate, endDate);

  return configs.options.some(k => k.toLowerCase() == lovKey.toLowerCase());
};

export const TenancyStatusControlsMapping = {
  [LeaseColumns.LeaseCompDetails_TenantStatus]: {
    startDate: TenancyColumnFields.StartDate,
    endDate: TenancyColumnFields.EndDate,
    occupancyDate: TenancyColumnFields.OccupancyDate,
  },
  [LeaseColumns.FloorSuite_Status]: {
    startDate: FloorSuiteTenancyColumnFields.StartDate,
    endDate: FloorSuiteTenancyColumnFields.EndDate,
    occupancyDate: FloorSuiteTenancyColumnFields.OccupancyDate,
  },
  [LeaseColumns.FloorSuite_StartDate]: FloorSuiteTenancyColumnFields.StartDate,
  [LeaseColumns.FloorSuite_EndDate]: FloorSuiteTenancyColumnFields.EndDate,
  [LeaseColumns.FloorSuite_OccupancyDate]: FloorSuiteTenancyColumnFields.OccupancyDate,
};

export const INVALID_TENANCY_STATUS_ERROR_KEY = 'invalidTenancyStatus';
